import { addMapKeys, setMapKeys } from "@features/survey/surveySlice";
import type {
  MapKey,
  MapKeyPost,
  MapKeyReorder,
  MapKeysBySurveyResponse,
} from "@survey-types/survey.mapkeys.types";
import { surveyApi } from "./base";
import { surveyCoreApi } from "./survey-core";

// Survey Map Keys API - handles map key management
export const surveyMapKeysApi = surveyApi
  .enhanceEndpoints({
    addTagTypes: ["SurveyMapKeys"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      addSurveyMapKeys: build.mutation<MapKeysBySurveyResponse, MapKeyPost>({
        query: (body) => ({
          url: `/mapkeys`,
          method: "POST",
          body,
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            data.mapKeys.forEach((mapKey) => dispatch(addMapKeys(mapKey)));
          } catch (_error) {
            console.error("Error in addSurveyMapKeys");
          }
        },
        invalidatesTags: (_result, _error, body) => [
          { type: "SurveyMapKeys", id: body.surveyId },
        ],
      }),
      getSurveyMapKey: build.query<MapKey, string>({
        query: (mapKeyId) => `/mapkeys/${mapKeyId}`,
        providesTags: (_result, _error, mapKeyId) => [
          { type: "SurveyMapKeys", id: mapKeyId },
        ],
      }),
      deleteSurveyMapKey: build.mutation<string, string>({
        query: (mapKeyId) => ({
          url: `/mapkeys/${mapKeyId}`,
          method: "DELETE",
        }),
      }),
      updateSurveyMapKey: build.mutation<
        MapKey,
        { mapKeyId: string; mapKey: Partial<MapKey>; surveyId: string }
      >({
        query: ({ mapKeyId, mapKey }) => ({
          url: `/mapkeys/${mapKeyId}`,
          method: "PATCH",
          body: mapKey,
        }),
        async onQueryStarted({ surveyId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            // Refresh updatedOn so the Share modal reflects map key label/toggle changes
            if (surveyId) {
              dispatch(
                surveyCoreApi.endpoints.updateSurveyTimestamp.initiate({
                  surveyId,
                }),
              );
            }
          } catch (_error) {
            console.error("Error in updateSurveyMapKey");
          }
        },
      }),
      reorderSurveyMapKey: build.mutation<
        MapKeysBySurveyResponse,
        MapKeyReorder
      >({
        query: (body) => ({
          url: `/mapkeys/reorder`,
          method: "PATCH",
          body,
        }),
        async onQueryStarted({ surveyId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            // Refresh updatedOn so the Share modal reflects the reordered map keys
            if (surveyId) {
              dispatch(
                surveyCoreApi.endpoints.updateSurveyTimestamp.initiate({
                  surveyId,
                }),
              );
            }
          } catch (_error) {
            console.error("Error in reorderSurveyMapKey");
          }
        },
      }),
      getSurveyMapKeysBySurvey: build.query<MapKeysBySurveyResponse, string>({
        query: (surveyId) => `/mapkeys/survey/${surveyId}`,
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(setMapKeys(data.mapKeys));
          } catch (_error) {
            console.error("Error in getSurveyMapKeysBySurvey");
          }
        },
        providesTags: (_result, _error, surveyId) => [
          { type: "SurveyMapKeys", id: surveyId },
        ],
      }),
    }),
  });

export const {
  useAddSurveyMapKeysMutation,
  useGetSurveyMapKeyQuery,
  useDeleteSurveyMapKeyMutation,
  useUpdateSurveyMapKeyMutation,
  useReorderSurveyMapKeyMutation,
  useGetSurveyMapKeysBySurveyQuery,
} = surveyMapKeysApi;
